import React from 'react';
import { useNavigate } from 'react-router-dom';
import './TermsOfService.css';

const LAST_UPDATED = 'April 14, 2026';

export default function TermsOfService() {
  const navigate = useNavigate();
  return (
    <div className="page tos">
      <div className="grid-bg" aria-hidden="true" />

      <header className="tos__header">
        <button className="page-back-btn" onClick={() => navigate(-1)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          Back
        </button>
        <h1 className="tos__title">Terms of Service</h1>
        <p className="tos__updated">Last updated: {LAST_UPDATED}</p>
      </header>

      <div className="tos__body">

        <Section title="1. Agreement to Terms">
          <p>These Terms of Service ("Terms") constitute a legally binding agreement between you and Hauoli & Maluhia LLC ("Company," "we," "us," or "our"), governing your access to and use of the StreamStack mobile and web application ("App"). By creating an account or using the App in any way, you confirm that you have read, understood, and agree to be bound by these Terms. If you do not agree, do not use the App.</p>
        </Section>

        <Section title="2. Eligibility">
          <p>You must be at least <strong>18 years of age</strong> to use StreamStack. By using the App you represent and warrant that you are 18 or older, have the legal capacity to enter into a binding contract, and are not prohibited from using the App under any applicable law. We reserve the right to terminate any account we believe is operated by a person under 18.</p>
        </Section>

        <Section title="3. Account Registration">
          <p>To access most features you must register an account. You agree to:</p>
          <ul>
            <li>Provide accurate, current, and complete information during registration.</li>
            <li>Maintain the security of your password and accept responsibility for all activity under your account.</li>
            <li>Notify us immediately at <strong>support@streamstack.us</strong> of any unauthorized use of your account.</li>
            <li>Not share, sell, transfer, or sublicense your account to any other person.</li>
          </ul>
          <p>We reserve the right to suspend or terminate accounts that contain false information or violate these Terms.</p>
        </Section>

        <Section title="4. The Stream Token Economy">
          <p><strong>Earned Streams</strong> are virtual tokens credited to your account when you log verified watch time on supported platforms (Instagram, TikTok, YouTube). Earned Streams may be redeemed for gift card rewards subject to the withdrawal terms below.</p>
          <p><strong>Purchased Streams</strong> are virtual tokens acquired through in-app purchases. Purchased Streams have no monetary value, cannot be withdrawn or redeemed for cash or gift cards, and are solely for use within the App's games and shop.</p>
          <p>Key rules:</p>
          <ul>
            <li><strong>Streams have no real-world monetary value.</strong> They are virtual tokens that exist solely within the App and do not represent real money in any form.</li>
            <li>Earned Streams are capped at <strong>300 per day</strong> from watch time sessions. Streams earned from games, ads, and daily bonuses do not count toward this cap.</li>
            <li>Stream values and earn rates are set by the Company and may change at any time with notice.</li>
            <li>Streams are not currency, cryptocurrency, or a financial instrument of any kind.</li>
            <li>Streams have no value outside the App and cannot be transferred between accounts.</li>
            <li>We reserve the right to adjust, expire, or void Streams earned through fraudulent or automated means.</li>
          </ul>
        </Section>

        <Section title="5. Withdrawals and Rewards">
          <p>Eligible users may redeem Earned Streams for gift card rewards subject to the following:</p>
          <ul>
            <li><strong>First withdrawal minimum:</strong> $5.00 USD equivalent in Earned Streams.</li>
            <li><strong>Subsequent withdrawal minimum:</strong> $10.00 USD equivalent in Earned Streams.</li>
            <li>A <strong>7-day cooling period</strong> applies between withdrawals.</li>
            <li>Rewards are fulfilled as gift cards (Amazon, Google Play, Visa Prepaid, and others as available). Available reward types may change without notice.</li>
            <li>Withdrawals are processed within a reasonable time frame, typically 3–10 business days.</li>
            <li>The Company reserves the right to withhold, delay, or deny any withdrawal if fraud, abuse, or a violation of these Terms is suspected.</li>
            <li>All rewards are final once issued. We are not responsible for lost, stolen, or expired gift cards once delivered.</li>
            <li>Reward values are determined at the time of redemption. Stream values may fluctuate.</li>
          </ul>
        </Section>

        <Section title="6. In-App Purchases">
          <p>The App may offer in-app purchases including Purchased Streams, cosmetic items, and other virtual goods. All purchases are:</p>
          <ul>
            <li>Final and non-refundable except where required by applicable law.</li>
            <li>Processed through third-party payment providers. We do not store your payment information.</li>
            <li>For virtual goods only — no physical goods are delivered.</li>
          </ul>
          <p>If you believe a charge was made in error, contact us at <strong>support@streamstack.us</strong> within 30 days of the transaction.</p>
        </Section>

        <Section title="7. Games and Chance-Based Features">
          <p>StreamStack includes games such as slot machines, scratch cards, chest openings, and spin wheels. These games use Streams (earned or purchased) as in-app currency. You acknowledge that:</p>
          <ul>
            <li>These games are for entertainment purposes and involve randomized outcomes.</li>
            <li>They do not constitute gambling as defined under applicable law since no real money is wagered directly.</li>
            <li>Past outcomes do not predict future results.</li>
            <li>The Company sets and may adjust payout rates and odds at any time.</li>
            <li>Purchased Streams spent in games cannot be recovered or refunded.</li>
          </ul>
        </Section>

        <Section title="8. Prohibited Conduct">
          <p>You agree not to:</p>
          <ul>
            <li>Use bots, scripts, automated tools, or any method to artificially inflate watch time or Stream earnings.</li>
            <li>Create multiple accounts to circumvent earning caps or withdrawal limits.</li>
            <li>Attempt to reverse engineer, decompile, or tamper with the App.</li>
            <li>Use the App for any unlawful purpose or in violation of any applicable law or regulation.</li>
            <li>Sell, trade, or transfer Streams or account access to other users or third parties.</li>
            <li>Submit false watch time reports or engage in any form of fraud.</li>
            <li>Harass, abuse, or harm other users.</li>
            <li>Attempt to gain unauthorized access to our systems or another user's account.</li>
          </ul>
          <p>Violation of any of the above may result in immediate account termination and forfeiture of all Streams and pending rewards without compensation.</p>
        </Section>

        <Section title="9. Intellectual Property">
          <p>All content, design, logos, graphics, code, and features within StreamStack are the exclusive property of Hauoli & Maluhia LLC or its licensors and are protected by applicable intellectual property laws. You are granted a limited, non-exclusive, non-transferable license to use the App for personal, non-commercial purposes. You may not reproduce, distribute, modify, or create derivative works without our express written consent.</p>
        </Section>

        <Section title="10. Third-Party Services">
          <p>The App integrates with third-party platforms and services including but not limited to Instagram, TikTok, YouTube, Google (Firebase), Unity Ads, and payment processors. Your use of those services is governed by their respective terms and privacy policies. We are not responsible for the content, availability, or practices of any third-party service.</p>
        </Section>

        <Section title="11. Privacy">
          <p>Your use of StreamStack is subject to our Privacy Policy, which is incorporated into these Terms by reference. By using the App you consent to the collection and use of your information as described in the Privacy Policy. For questions contact <strong>support@streamstack.us</strong>.</p>
        </Section>

        <Section title="12. Disclaimers">
          <p>THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. HAUOLI & MALUHIA LLC DOES NOT WARRANT THAT THE APP WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS. WE DO NOT GUARANTEE THAT ANY SPECIFIC AMOUNT OF STREAMS WILL BE AVAILABLE OR THAT REWARDS WILL ALWAYS BE AVAILABLE IN YOUR REGION.</p>
        </Section>

        <Section title="13. Limitation of Liability">
          <p>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, HAUOLI & MALUHIA LLC AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF THE APP, INCLUDING LOSS OF STREAMS, REWARDS, DATA, OR REVENUE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. OUR TOTAL LIABILITY TO YOU FOR ANY CLAIM SHALL NOT EXCEED THE GREATER OF $50 USD OR THE AMOUNT YOU PAID TO US IN THE 90 DAYS PRECEDING THE CLAIM.</p>
        </Section>

        <Section title="14. Indemnification">
          <p>You agree to indemnify, defend, and hold harmless Hauoli & Maluhia LLC and its affiliates, officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of your use of the App, your violation of these Terms, or your violation of any rights of a third party.</p>
        </Section>

        <Section title="15. Termination">
          <p>We may suspend or terminate your account at any time, with or without notice, for any reason including violation of these Terms. Upon termination, your right to use the App ceases immediately and any pending or accrued Streams may be forfeited at our discretion. You may also delete your account at any time by contacting <strong>support@streamstack.us</strong>.</p>
        </Section>

        <Section title="16. Governing Law and Dispute Resolution">
          <p>These Terms are governed by and construed in accordance with the laws of the State of Georgia, United States, without regard to conflict of law principles. Any dispute arising from these Terms or your use of the App shall first be attempted to be resolved informally by contacting us at <strong>support@streamstack.us</strong>. If not resolved within 30 days, disputes shall be submitted to binding arbitration in Georgia under the rules of the American Arbitration Association. You waive any right to participate in a class action lawsuit or class-wide arbitration.</p>
        </Section>

        <Section title="17. Changes to These Terms">
          <p>We reserve the right to update or modify these Terms at any time. We will notify you of material changes by updating the "Last updated" date at the top of this page and, where appropriate, by in-app notification. Your continued use of the App after changes take effect constitutes your acceptance of the revised Terms.</p>
        </Section>

        <Section title="18. Contact Us">
          <p>If you have any questions, concerns, or requests regarding these Terms, please contact us at:</p>
          <p><strong>Hauoli & Maluhia LLC</strong><br />
          Email: <strong>support@streamstack.us</strong><br />
          State of incorporation: Georgia, United States</p>
        </Section>

      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="tos__section">
      <h2 className="tos__section-title">{title}</h2>
      <div className="tos__section-body">{children}</div>
    </div>
  );
}
